import { dashboardService } from '../database/dashboard';
import { invoiceService } from '../database/invoices';
import { quoteService } from '../database/quotes';
import { clientService } from '../database/clients';
import { paymentService } from '../database/payments';
import { expenseService } from '../database/expenses';
import { formatCurrency, formatDate } from '../utils';

interface UserContext {
  id?: string;
  email?: string;
  full_name?: string;
  role?: 'admin' | 'client';
}

class AIService {
  private async getUserData(user: UserContext | null) {
    if (!user) return null;

    try {
      const [
        dashboardStats,
        invoices,
        quotes,
        clients,
        payments,
        expenses
      ] = await Promise.all([
        dashboardService.getDashboardStats(),
        invoiceService.getInvoices(),
        quoteService.getQuotes(),
        clientService.getClients(),
        paymentService.getPayments(),
        expenseService.getExpenses()
      ]);

      return {
        user,
        stats: dashboardStats.data,
        invoices: invoices.data || [],
        quotes: quotes.data || [],
        clients: clients.data || [],
        payments: payments.data || [],
        expenses: expenses.data || []
      };
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }

  private isPortalRelatedQuery(query: string): boolean {
    const portalKeywords = [
      'invoice', 'quote', 'client', 'payment', 'expense', 'dashboard',
      'account', 'profile', 'portal', 'crm', 'business', 'revenue',
      'outstanding', 'overdue', 'paid', 'draft', 'sent', 'approved',
      'how to', 'help', 'create', 'add', 'edit', 'delete', 'view',
      'search', 'filter', 'export', 'total', 'amount', 'status',
      'due date', 'vendor', 'category', 'method', 'reference'
    ];

    const queryLower = query.toLowerCase();
    return portalKeywords.some(keyword => queryLower.includes(keyword));
  }

  private generateResponse(query: string, userData: any): string {
    const queryLower = query.toLowerCase();

    // Check if query is portal-related
    if (!this.isPortalRelatedQuery(query)) {
      return "I'm here to help you with questions about your account data and portal features. I can assist with information about your invoices, quotes, clients, payments, expenses, and how to use the various features of this CRM system. Please ask me something related to your business data or portal functionality.";
    }

    // Dashboard and overview queries
    if (queryLower.includes('dashboard') || queryLower.includes('overview') || queryLower.includes('summary')) {
      return this.getDashboardSummary(userData);
    }

    // Invoice queries
    if (queryLower.includes('invoice')) {
      if (queryLower.includes('total') || queryLower.includes('how many')) {
        return this.getInvoiceSummary(userData);
      }
      if (queryLower.includes('overdue')) {
        return this.getOverdueInvoices(userData);
      }
      if (queryLower.includes('paid')) {
        return this.getPaidInvoices(userData);
      }
      if (queryLower.includes('create') || queryLower.includes('add') || queryLower.includes('how to')) {
        return this.getInvoiceHelp();
      }
      return this.getInvoiceSummary(userData);
    }

    // Quote queries
    if (queryLower.includes('quote')) {
      if (queryLower.includes('approved')) {
        return this.getApprovedQuotes(userData);
      }
      if (queryLower.includes('convert') || queryLower.includes('invoice')) {
        return this.getQuoteConversionHelp();
      }
      if (queryLower.includes('create') || queryLower.includes('how to')) {
        return this.getQuoteHelp();
      }
      return this.getQuoteSummary(userData);
    }

    // Client queries
    if (queryLower.includes('client') || queryLower.includes('customer')) {
      if (queryLower.includes('add') || queryLower.includes('create') || queryLower.includes('how to')) {
        return this.getClientHelp();
      }
      return this.getClientSummary(userData);
    }

    // Payment queries
    if (queryLower.includes('payment')) {
      if (queryLower.includes('record') || queryLower.includes('add') || queryLower.includes('how to')) {
        return this.getPaymentHelp();
      }
      return this.getPaymentSummary(userData);
    }

    // Expense queries
    if (queryLower.includes('expense')) {
      if (queryLower.includes('add') || queryLower.includes('create') || queryLower.includes('how to')) {
        return this.getExpenseHelp();
      }
      return this.getExpenseSummary(userData);
    }

    // Revenue and financial queries
    if (queryLower.includes('revenue') || queryLower.includes('income') || queryLower.includes('profit')) {
      return this.getFinancialSummary(userData);
    }

    // Outstanding amount queries
    if (queryLower.includes('outstanding') || queryLower.includes('pending')) {
      return this.getOutstandingSummary(userData);
    }

    // Search and filter help
    if (queryLower.includes('search') || queryLower.includes('filter') || queryLower.includes('find')) {
      return this.getSearchHelp();
    }

    // General help
    if (queryLower.includes('help') || queryLower.includes('how to use') || queryLower.includes('tutorial')) {
      return this.getGeneralHelp(userData.user);
    }

    // Default response for portal-related queries
    return this.getDefaultPortalResponse(userData);
  }

  private getDashboardSummary(userData: any): string {
    const { stats, invoices, user } = userData;
    
    if (user.role === 'client') {
      const clientInvoices = invoices.filter((inv: any) => inv.client?.email === user.email);
      const totalOwed = clientInvoices
        .filter((inv: any) => inv.status !== 'paid')
        .reduce((sum: number, inv: any) => sum + Number(inv.total), 0);
      
      return `Here's your account overview:

📊 **Your Account Summary:**
• Total invoices: ${clientInvoices.length}
• Outstanding amount: ${formatCurrency(totalOwed)}
• Paid invoices: ${clientInvoices.filter((inv: any) => inv.status === 'paid').length}

You can view all your invoices, make payments, and request quotes through your client portal. Is there anything specific you'd like to know about your account?`;
    }

    return `Here's your business dashboard overview:

📊 **Business Summary:**
• Total revenue: ${formatCurrency(stats?.totalRevenue || 0)}
• Outstanding amount: ${formatCurrency(stats?.totalOutstanding || 0)}
• Total expenses: ${formatCurrency(stats?.totalExpenses || 0)}
• Net profit: ${formatCurrency((stats?.totalRevenue || 0) - (stats?.totalExpenses || 0))}

📋 **Invoice Status:**
• Total invoices: ${stats?.invoiceCount || 0}
• Paid: ${stats?.paidInvoices || 0}
• Overdue: ${stats?.overdueInvoices || 0}

Your business is performing well! Would you like more details about any specific area?`;
  }

  private getInvoiceSummary(userData: any): string {
    const { invoices, user } = userData;
    
    if (user.role === 'client') {
      const clientInvoices = invoices.filter((inv: any) => inv.client?.email === user.email);
      return `Here are your invoice details:

📄 **Your Invoices:**
• Total invoices: ${clientInvoices.length}
• Paid: ${clientInvoices.filter((inv: any) => inv.status === 'paid').length}
• Pending: ${clientInvoices.filter((inv: any) => inv.status === 'sent').length}
• Overdue: ${clientInvoices.filter((inv: any) => inv.status === 'overdue').length}

You can view detailed information about each invoice in your client portal. Would you like help with making a payment?`;
    }

    const totalAmount = invoices.reduce((sum: number, inv: any) => sum + Number(inv.total), 0);
    
    return `Here's your invoice overview:

📄 **Invoice Summary:**
• Total invoices: ${invoices.length}
• Total value: ${formatCurrency(totalAmount)}
• Paid: ${invoices.filter((inv: any) => inv.status === 'paid').length}
• Sent: ${invoices.filter((inv: any) => inv.status === 'sent').length}
• Draft: ${invoices.filter((inv: any) => inv.status === 'draft').length}
• Overdue: ${invoices.filter((inv: any) => inv.status === 'overdue').length}

You can create, edit, and manage invoices from the Invoices section. Need help with creating a new invoice?`;
  }

  private getOverdueInvoices(userData: any): string {
    const { invoices } = userData;
    const overdueInvoices = invoices.filter((inv: any) => inv.status === 'overdue');
    
    if (overdueInvoices.length === 0) {
      return "Great news! You don't have any overdue invoices at the moment. All your invoices are up to date.";
    }

    const overdueAmount = overdueInvoices.reduce((sum: number, inv: any) => sum + Number(inv.total), 0);
    
    return `⚠️ **Overdue Invoices Alert:**

You have ${overdueInvoices.length} overdue invoice(s) totaling ${formatCurrency(overdueAmount)}.

**Recent overdue invoices:**
${overdueInvoices.slice(0, 3).map((inv: any) => 
  `• ${inv.invoice_number} - ${inv.client?.name} - ${formatCurrency(Number(inv.total))}`
).join('\n')}

I recommend following up with these clients promptly. You can send reminders or mark invoices as paid from the Invoices section.`;
  }

  private getPaidInvoices(userData: any): string {
    const { invoices } = userData;
    const paidInvoices = invoices.filter((inv: any) => inv.status === 'paid');
    const paidAmount = paidInvoices.reduce((sum: number, inv: any) => sum + Number(inv.total), 0);
    
    return `✅ **Paid Invoices:**

You have ${paidInvoices.length} paid invoice(s) totaling ${formatCurrency(paidAmount)}.

This represents ${((paidInvoices.length / invoices.length) * 100).toFixed(1)}% of your total invoices. Great job on maintaining good payment collection!`;
  }

  private getQuoteSummary(userData: any): string {
    const { quotes } = userData;
    const totalValue = quotes.reduce((sum: number, quote: any) => sum + Number(quote.total), 0);
    
    return `💼 **Quote Summary:**

• Total quotes: ${quotes.length}
• Total value: ${formatCurrency(totalValue)}
• Approved: ${quotes.filter((q: any) => q.status === 'approved').length}
• Sent: ${quotes.filter((q: any) => q.status === 'sent').length}
• Draft: ${quotes.filter((q: any) => q.status === 'draft').length}
• Rejected: ${quotes.filter((q: any) => q.status === 'rejected').length}

Approved quotes can be converted to invoices. Would you like help with the conversion process?`;
  }

  private getApprovedQuotes(userData: any): string {
    const { quotes } = userData;
    const approvedQuotes = quotes.filter((q: any) => q.status === 'approved');
    
    if (approvedQuotes.length === 0) {
      return "You don't have any approved quotes at the moment. Once clients approve your quotes, you can convert them to invoices.";
    }

    const approvedValue = approvedQuotes.reduce((sum: number, quote: any) => sum + Number(quote.total), 0);
    
    return `✅ **Approved Quotes:**

You have ${approvedQuotes.length} approved quote(s) worth ${formatCurrency(approvedValue)}.

**Ready for conversion:**
${approvedQuotes.slice(0, 3).map((quote: any) => 
  `• ${quote.quote_number} - ${quote.client?.name} - ${formatCurrency(Number(quote.total))}`
).join('\n')}

These quotes are ready to be converted to invoices. Click "Convert to Invoice" in the quote details to proceed.`;
  }

  private getClientSummary(userData: any): string {
    const { clients, invoices } = userData;
    
    return `👥 **Client Summary:**

• Total clients: ${clients.length}
• Active clients: ${new Set(invoices.map((inv: any) => inv.client_id)).size}

**Recent clients:**
${clients.slice(0, 3).map((client: any) => 
  `• ${client.name}${client.company ? ` (${client.company})` : ''}`
).join('\n')}

You can manage client information, add new clients, and view their invoice history from the Clients section.`;
  }

  private getPaymentSummary(userData: any): string {
    const { payments } = userData;
    const totalReceived = payments.reduce((sum: number, payment: any) => sum + Number(payment.amount), 0);
    
    return `💰 **Payment Summary:**

• Total payments received: ${payments.length}
• Total amount: ${formatCurrency(totalReceived)}

**Payment methods:**
• Card: ${payments.filter((p: any) => p.method === 'card').length}
• Bank Transfer: ${payments.filter((p: any) => p.method === 'bank_transfer').length}
• UPI: ${payments.filter((p: any) => p.method === 'upi').length}
• Cash: ${payments.filter((p: any) => p.method === 'cash').length}
• Cheque: ${payments.filter((p: any) => p.method === 'cheque').length}

You can record new payments and view payment history from the Payments section.`;
  }

  private getExpenseSummary(userData: any): string {
    const { expenses } = userData;
    const totalExpenses = expenses.reduce((sum: number, expense: any) => sum + Number(expense.amount), 0);
    const categories = [...new Set(expenses.map((exp: any) => exp.category))];
    
    return `📊 **Expense Summary:**

• Total expenses: ${expenses.length}
• Total amount: ${formatCurrency(totalExpenses)}
• Categories: ${categories.length}

**Top categories:**
${categories.slice(0, 5).map(category => {
  const categoryExpenses = expenses.filter((exp: any) => exp.category === category);
  const categoryTotal = categoryExpenses.reduce((sum: number, exp: any) => sum + Number(exp.amount), 0);
  return `• ${category}: ${formatCurrency(categoryTotal)}`;
}).join('\n')}

Track and manage your business expenses from the Expenses section.`;
  }

  private getFinancialSummary(userData: any): string {
    const { stats } = userData;
    const netProfit = (stats?.totalRevenue || 0) - (stats?.totalExpenses || 0);
    const profitMargin = stats?.totalRevenue ? ((netProfit / stats.totalRevenue) * 100).toFixed(1) : '0';
    
    return `💹 **Financial Overview:**

• Total Revenue: ${formatCurrency(stats?.totalRevenue || 0)}
• Total Expenses: ${formatCurrency(stats?.totalExpenses || 0)}
• Net Profit: ${formatCurrency(netProfit)}
• Profit Margin: ${profitMargin}%

${netProfit > 0 ? '🎉 Your business is profitable!' : '⚠️ Consider reviewing expenses to improve profitability.'}

You can view detailed financial reports and trends from the Dashboard.`;
  }

  private getOutstandingSummary(userData: any): string {
    const { stats, invoices } = userData;
    const outstandingInvoices = invoices.filter((inv: any) => inv.status !== 'paid');
    
    return `⏳ **Outstanding Payments:**

• Outstanding amount: ${formatCurrency(stats?.totalOutstanding || 0)}
• Pending invoices: ${outstandingInvoices.length}

**Breakdown by status:**
• Sent: ${invoices.filter((inv: any) => inv.status === 'sent').length}
• Overdue: ${invoices.filter((inv: any) => inv.status === 'overdue').length}

Consider following up on overdue invoices to improve cash flow. You can send payment reminders from the invoice details.`;
  }

  private getInvoiceHelp(): string {
    return `📄 **How to Create an Invoice:**

1. **Navigate to Invoices** → Click "Create Invoice"
2. **Select Client** → Choose from existing clients
3. **Set Due Date** → Pick when payment is due
4. **Add Items** → Include description, quantity, and rate
5. **Review Totals** → Check subtotal, tax, and total
6. **Add Notes** → Optional terms or conditions
7. **Save** → Invoice will be created with auto-generated number

**Tips:**
• Tax rate defaults to 18% (GST)
• Quantities must be whole numbers
• You can add multiple items
• Save as draft to edit later

Need help with a specific step?`;
  }

  private getQuoteHelp(): string {
    return `💼 **How to Create a Quote:**

1. **Navigate to Quotes** → Click "Create Quote"
2. **Select Client** → Choose from existing clients
3. **Set Valid Until** → Quote expiration date
4. **Add Items** → Include description, quantity, and rate
5. **Review Totals** → Check subtotal, tax, and total
6. **Add Notes** → Optional terms or conditions
7. **Save** → Quote will be created with auto-generated number

**Quote Workflow:**
• Draft → Send → Approved/Rejected
• Approved quotes can be converted to invoices
• Set realistic validity periods

Would you like help with converting quotes to invoices?`;
  }

  private getQuoteConversionHelp(): string {
    return `🔄 **Converting Quotes to Invoices:**

**Prerequisites:**
• Quote must be in "Approved" status
• Client has accepted the quote terms

**Steps:**
1. **Open Quote** → View the approved quote
2. **Click "Convert to Invoice"** → Beautiful conversion dialog appears
3. **Set Due Date** → Choose invoice payment deadline
4. **Confirm Conversion** → All items will be copied

**What Happens:**
• New invoice created with same items and amounts
• Quote status updated to "Approved"
• Client and pricing remain identical
• Invoice gets auto-generated number

The conversion process is seamless and maintains all your quote details!`;
  }

  private getClientHelp(): string {
    return `👥 **How to Add a Client:**

1. **Navigate to Clients** → Click "Add Client"
2. **Basic Info** → Name and email (required)
3. **Company Details** → Company name (optional)
4. **Contact Info** → Phone and address (optional)
5. **Tax Info** → GSTIN for GST compliance (optional)
6. **Save** → Client will be available for invoices/quotes

**Tips:**
• Email must be unique
• Use proper GSTIN format: 22AAAAA0000A1Z5
• Complete profiles help with professional invoices
• You can edit client details anytime

**Client Management:**
• Search clients by name, email, or company
• View client's invoice history
• Edit or delete client records

Need help with client data management?`;
  }

  private getPaymentHelp(): string {
    return `💰 **How to Record a Payment:**

1. **Navigate to Payments** → Click "Record Payment"
2. **Select Invoice** → Choose unpaid invoice
3. **Enter Amount** → Cannot exceed invoice total
4. **Payment Method** → Cash, Card, UPI, Bank Transfer, Cheque
5. **Reference ID** → Transaction ID or cheque number
6. **Payment Date** → When payment was received
7. **Notes** → Optional payment details
8. **Save** → Payment recorded and invoice updated

**Auto-Features:**
• Invoice marked as "Paid" when fully paid
• Payment history tracked
• Multiple partial payments supported

**Payment Methods:**
• Card, UPI for digital payments
• Cash for in-person transactions
• Bank Transfer for business payments
• Cheque for traditional payments

Need help with payment tracking?`;
  }

  private getExpenseHelp(): string {
    return `📊 **How to Add an Expense:**

1. **Navigate to Expenses** → Click "Add Expense"
2. **Date** → When expense occurred
3. **Amount** → Expense value
4. **Category** → Choose from common categories or create custom
5. **Vendor** → Who you paid (supplier, service provider)
6. **Receipt URL** → Optional link to receipt/invoice
7. **Notes** → Additional expense details
8. **Save** → Expense recorded for tracking

**Categories Include:**
• Office Supplies, Travel, Marketing
• Software, Equipment, Utilities
• Rent, Insurance, Professional Services
• Custom categories for specific needs

**Benefits:**
• Track business spending
• Categorize for tax purposes
• Monitor expense trends
• Calculate profit margins

Would you like help with expense categorization?`;
  }

  private getSearchHelp(): string {
    return `🔍 **Search & Filter Features:**

**Available in All Modules:**
• **Invoices** → Search by number, client, amount, notes
• **Quotes** → Search by number, client, amount, notes  
• **Clients** → Search by name, email, company, phone
• **Payments** → Search by invoice, client, reference ID
• **Expenses** → Search by vendor, category, notes

**How to Search:**
1. **Use Search Bar** → Type keywords in search field
2. **Real-time Results** → Instant filtering as you type
3. **Combine Filters** → Use search + status/category filters
4. **Clear Search** → Click clear or delete search terms

**Filter Options:**
• **Status Filters** → Draft, Sent, Paid, Overdue
• **Category Filters** → Expense categories
• **Method Filters** → Payment methods

**Tips:**
• Search is case-insensitive
• Partial matches work
• Search across multiple fields simultaneously

Need help finding specific records?`;
  }

  private getGeneralHelp(user: any): string {
    if (user.role === 'client') {
      return `🏠 **Client Portal Guide:**

**Your Dashboard:**
• View account overview and recent activity
• Check outstanding balances and payment history
• Access invoices and quotes

**Available Features:**
• **My Invoices** → View and pay outstanding invoices
• **My Quotes** → Review quotes and request new ones
• **Payment History** → Track all your payments
• **Support** → Get help when needed

**Quick Actions:**
• Pay invoices online
• Download invoice PDFs
• Request new quotes
• Update account information

**Need Help?**
• Use this AI assistant for instant help
• Contact support for complex issues
• Check your email for invoice notifications

Is there a specific feature you'd like to learn about?`;
    }

    return `🎯 **Admin Portal Guide:**

**Main Sections:**
• **Dashboard** → Business overview and analytics
• **Invoices** → Create, manage, and track invoices
• **Quotes** → Create quotes and convert to invoices
• **Clients** → Manage customer database
• **Payments** → Record and track payments
• **Expenses** → Track business expenses

**Key Features:**
• **Search & Filter** → Find records quickly
• **Auto-numbering** → Invoices and quotes auto-numbered
• **Status Tracking** → Monitor invoice/quote progress
• **Financial Reports** → Revenue, expenses, profit tracking

**Workflow:**
1. Add clients to database
2. Create quotes for potential work
3. Convert approved quotes to invoices
4. Record payments when received
5. Track expenses for profitability

**Pro Tips:**
• Use search to find records quickly
• Set realistic due dates for invoices
• Follow up on overdue payments
• Categorize expenses for better tracking

What specific area would you like help with?`;
  }

  private getDefaultPortalResponse(userData: any): string {
    const { user } = userData;
    
    return `I'm here to help you with your ${user.role === 'admin' ? 'business management' : 'account'} needs! 

**I can help you with:**
• Account and dashboard information
• Invoice and quote details
• Client and payment data
• Expense tracking (admin)
• Portal feature guidance
• Search and filter tips

**Try asking me:**
• "Show me my dashboard summary"
• "How many overdue invoices do I have?"
• "How do I create a new invoice?"
• "What's my total revenue?"
• "How do I search for clients?"

What would you like to know about your ${user.role === 'admin' ? 'business' : 'account'}?`;
  }

  async processQuery(query: string, user: UserContext | null): Promise<string> {
    try {
      // Get user data
      const userData = await this.getUserData(user);
      
      if (!userData) {
        return "I apologize, but I'm having trouble accessing your account data at the moment. Please try again in a few moments, or contact support if the issue persists.";
      }

      // Generate response based on query and user data
      return this.generateResponse(query, userData);
      
    } catch (error) {
      console.error('AI Service error:', error);
      return "I apologize, but I encountered an error while processing your request. Please try again, or contact support if the problem continues.";
    }
  }
}

export const aiService = new AIService();